/**
 * `RESEQUENCE_MASTER` step. Operates on the `cseId` axis (master rows are
 * CSE-keyed). Stops are deduped + monotonically renumbered via
 * `buildSequencePairs`.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type {
  Deps,
  MasterStop,
  PipelineStep,
} from 'types/modules/routing'

import { buildMasterResequencePayload } from '../payloads'
import type { StepResult } from '../types'

export interface SequenceMasterStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly orderedStops: ReadonlyArray<MasterStop>
}

export type SequenceMasterStepValue = { readonly committed: true }

export type SequenceMasterStepResult = StepResult<'RESEQUENCE_MASTER', SequenceMasterStepValue>

export const makeSequenceMasterStep = (
  args: SequenceMasterStepArgs,
  deps: Deps,
): PipelineStep<'RESEQUENCE_MASTER', SequenceMasterStepResult> => {
  const stepId = `sequence-master:${args.routeId}:${args.dayOfWeek}`
  const dayOfWeek = args.dayOfWeek as ApiDayOfWeek
  return {
    id: stepId,
    phase: 'RESEQUENCE_MASTER',
    label: 'Failed to resequence master sequence',
    applyOrder: 500,
    run: async (ctx) => {
      if (args.orderedStops.length === 0) {
        return ok({ phase: 'RESEQUENCE_MASTER', stepId, value: { committed: true } })
      }
      const rows = args.orderedStops.map(s => ({ id: String(s.configuredServiceEventId) }))
      const payload = buildMasterResequencePayload(rows, 'id', { mode: 'strict' })
      if (!payload.ok) return err(payload.error)
      try {
        await deps
          .sequenceMaster({
            routeId: args.routeId,
            data: {
              dayOfWeek,
              sequence: payload.value.pairs.map(p => ({
                stopId: String(p.stopId),
                sequence: p.sequence,
              })),
              versionContext: ctx.versionContext,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({ phase: 'RESEQUENCE_MASTER', stepId, value: { committed: true } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'RESEQUENCE_MASTER',
          status,
          message,
        })
      }
    },
  }
}
